import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { displayDate } from '../common/datePresentation';
import { fullName, money } from './clientPortalSelectors';
import { billingYearLabel } from './clientBillingPresentation';

export function LessonLine({ item }) {
  const { t: tr, i18n } = useTranslation();
  const currency = value => money(Number.isFinite(value) ? value / 100 : null, i18n.language);
  const discount = item.discountAmountCents > 0 && item.discount;
  const percent = discount && Number.isFinite(discount.percentBasisPoints)
    ? new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 2 }).format(discount.percentBasisPoints / 10000) : '';
  return (
    <div className="portal-billing__lesson">
      <div>{displayDate(item.date, i18n.language)}<small>{item.startTime}</small></div>
      <div>
        <strong>{item.studentName || tr('client:billing.studentUnavailable')}</strong>
        <small>{item.classTypeName || tr('client:billing.lessonUnavailable')}</small>
        <small>{tr('client:billing.teacher')}: {item.teacherName || tr('client:billing.notRecorded')}</small>
        {discount && <small className="portal-billing__discount">{discount.name || tr('client:billing.discount')} {percent} · −{currency(item.discountAmountCents)}</small>}
      </div>
      <div className="portal-billing__price">
        <strong>{currency(item.amountCents)}</strong>
        {discount && Number.isFinite(item.baseAmountCents) && <small>{tr('client:billing.beforeDiscount')}<br />{currency(item.baseAmountCents)}</small>}
      </div>
    </div>
  );
}
LessonLine.propTypes = { item: PropTypes.object.isRequired };

export default function BillingEntryDetails({ entry, client }) {
  const { t: tr, i18n } = useTranslation();
  const currency = value => money(Number.isFinite(value) ? value / 100 : null, i18n.language);
  const details = entry.billingDetails;
  const payment = entry.type === 'PAYMENT';
  const charge = entry.type === 'MONTHLY_CHARGE';
  const method = ['TRANSFER', 'CASH', 'CARD'].includes(entry.method) ? entry.method : 'OTHER';
  const reasonKeys = ['firstFreeLesson', 'packageConsumed', 'lessonCancelled', 'cancelledOnTime', 'cancelledLate', 'absent', 'excluded'];
  const charged = details ? details.charged || [] : [];
  const skipped = details ? details.skipped || [] : [];
  const fees = details ? details.extraFees || [] : [];
  const discounts = charged.reduce((sum, item) => sum + (item.discountAmountCents || 0), 0);
  const lessonTotal = charged.every(item => Number.isFinite(item.amountCents)) ? charged.reduce((sum, item) => sum + item.amountCents, 0) : null;
  return (
    <details className="portal-billing__details">
      <summary>{tr('client:billing.details')}</summary>
      <div className="portal-billing__detail-content">
        <dl className="portal-billing__metadata">
          <div><dt>{tr('client:billing.date')}</dt><dd>{displayDate(entry.date, i18n.language) || tr('client:billing.notRecorded')}</dd></div>
          <div><dt>{tr('client:billing.year')}</dt><dd>{entry.schoolYearStartYear ? billingYearLabel(entry.schoolYearStartYear) : tr('client:billing.notRecorded')}</dd></div>
          {payment && <div><dt>{tr('client:billing.method')}</dt><dd>{entry.method ? tr(`client:billing.methods.${method}`) : tr('client:billing.notRecorded')}</dd></div>}
          {payment && <div><dt>{tr('client:billing.payer')}</dt><dd>{fullName(client)}</dd></div>}
          {charge && entry.settlementMonth && <div><dt>{tr('client:billing.period')}</dt><dd>{entry.settlementMonth.split('-').reverse().join('.')}</dd></div>}
        </dl>
        {charge && !details && <p>{tr('client:billing.detailsUnavailable')}</p>}
        {charge && details && (
          <>
            {charged.length > 0 && <h3>{tr('client:billing.chargedLessons')}</h3>}
            {charged.map(item => <LessonLine key={item.key} item={item} />)}
            {discounts > 0 && <p className="portal-billing__sum portal-billing__discount"><span>{tr('client:billing.discountsTotal')}</span><strong>−{currency(discounts)}</strong></p>}
            {charged.length > 0 && <p className="portal-billing__sum"><span>{tr('client:billing.lessonsTotal')}</span><strong>{currency(lessonTotal)}</strong></p>}
            {skipped.length > 0 && (
            <details>
              <summary>{tr('client:billing.skippedLessons', { count: skipped.length })}</summary>
              {skipped.map(item => (
                <div key={item.key}>
                  <LessonLine item={item} />
                  <p className="text-muted">{tr(`client:billing.reasons.${reasonKeys.includes(item.reason) ? item.reason : 'other'}`)}</p>
                </div>
              ))}
            </details>
            )}
            {fees.length > 0 && <h3>{tr('client:billing.extraFees')}</h3>}
            {fees.map(fee => (
              <div className="portal-billing__lesson" key={fee.key}>
                <div>{displayDate(fee.date, i18n.language)}</div><div><strong>{fee.description}</strong><small>{fee.studentName}</small></div>
                <strong className="portal-billing__price">{currency(fee.amountCents)}</strong>
              </div>
            ))}
            <p className="portal-billing__sum"><strong>{tr('client:billing.chargeTotal')}</strong><strong>{money(Math.abs(entry.amount), i18n.language)}</strong></p>
          </>
        )}
      </div>
    </details>
  );
}
BillingEntryDetails.propTypes = { entry: PropTypes.object.isRequired, client: PropTypes.object.isRequired };
